import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Label, Select } from '@kcftech/design-system';
import { AssetCategoryParameterType } from '@kcftech/prometheustypescriptapiclient';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { TextInput } from 'components/forms/TextInput';
import {
    baseDefaultValueRules,
    validValueMaxLength,
} from 'pages/Settings/AssetType/parameter_form/shared';
import {
    AssetParameterFormData,
    AssetParameterFormInputProps,
} from 'pages/Settings/AssetType/parameter_form/types';
import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import { RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { sortBy } from 'utils/array';

const ValidValueInputContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2rem;
`;

const ValidValuesListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin: 2rem 2rem 1rem 2rem;
`;

const getDefaultValueRules = (
    validValues: string[] | null
): RegisterOptions<AssetParameterFormData, 'defaultValue'> => ({
    ...baseDefaultValueRules,
    validate: (value) => {
        if (!value) {
            return undefined;
        }
        if (!validValues?.includes(value)) {
            return 'Default value must be in selections';
        }
        return undefined;
    },
});

const sortValues = (values: string[]) => sortBy(values, (value) => value);

export function AssetParameterSelectionFormContent({
    form,
    parameter,
}: AssetParameterFormInputProps) {
    const [validValue, setValidValue] = useState('');
    const validValueTrimmed = useMemo(() => validValue.trim(), [validValue]);

    const {
        register,
        formState: { errors },
        setValue,
        watch,
    } = form;

    const [type, validValues] = watch(['type', 'validValues']);

    const options = useMemo(
        () => [
            { title: 'None', value: '' },
            ...(validValues ?? []).map((value) => ({ title: value, value })),
        ],
        [validValues]
    );

    const inputIsValid = useMemo(
        () =>
            validValueTrimmed.length > 0 &&
            (!validValues || !validValues.includes(validValueTrimmed)),
        [validValueTrimmed, validValues]
    );

    const onAddValidValue = () => {
        if (inputIsValid) {
            const list = validValues ?? [];
            setValue('validValues', sortValues([...list, validValueTrimmed]));
            setValidValue('');
        }
    };

    const onRemoveValidValue = (removeValue: string) => {
        const list = validValues ?? [];
        setValue(
            'validValues',
            list.filter((value) => value !== removeValue)
        );
    };

    const onTextInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.code === 'Enter') {
            onAddValidValue();
            event.preventDefault();
            event.stopPropagation();
        }
    };

    useEffect(() => {
        if (type !== AssetCategoryParameterType.Selection) {
            throw new Error('Invalid type');
        }

        if (parameter?.CategoryParameterType === AssetCategoryParameterType.Selection) {
            setValue('validValues', sortValues(parameter.ValidValues ?? []));
            setValue('defaultValue', parameter.DefaultValue ?? '');
        } else {
            setValue('validValues', []);
            setValue('defaultValue', '');
        }

        return () => {
            setValue('validValues', null);
            setValue('defaultValue', '');
        };
    }, [
        parameter?.CategoryParameterType,
        parameter?.DefaultValue,
        parameter?.ValidValues,
        setValue,
        type,
    ]);

    return (
        <>
            <div>
                <Label label="Selections">
                    <ValidValueInputContainer>
                        <TextInput
                            placeholder="Enter a selection value"
                            width="46rem"
                            maxLength={validValueMaxLength}
                            value={validValue}
                            onChange={(event) => setValidValue(event.target.value)}
                            onKeyDown={onTextInputKeyDown}
                        />
                        <Button
                            type="button"
                            appearance="subtle"
                            spacing="compact"
                            icon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={onAddValidValue}
                            disabled={!inputIsValid}
                        >
                            Add
                        </Button>
                    </ValidValueInputContainer>
                </Label>
                {validValues && validValues.length !== 0 && (
                    <ValidValuesListContainer>
                        {validValues.map((value) => (
                            <div className="flex justify-between" key={value}>
                                <div className="long-text-wrap">{value}</div>
                                <HoverIconButton
                                    icon={faTrash}
                                    hoverIcon={faTrashSolid}
                                    color={tokens.text.secondary}
                                    hoverColor={tokens.error.default}
                                    size="14"
                                    onClick={() => onRemoveValidValue(value)}
                                />
                            </div>
                        ))}
                    </ValidValuesListContainer>
                )}
            </div>
            <Select
                label="Default Selection"
                error={errors.defaultValue?.message}
                options={options}
                {...register('defaultValue', getDefaultValueRules(validValues))}
            />
        </>
    );
}
